<template>
  <div class="tab-content">
    <!-- Header -->
    <div class="main-header">
      <div>
        <h1>{{ $t("controlCenter.sidebar.userManagement") }}</h1>
        <p class="subtitle">
          {{ $t("controlCenter.userManagement.subtitle") }}
        </p>
      </div>
    </div>

    <!-- Main Card with User List -->
    <div class="card">
      <div class="card-header">
        <div class="search-controls">
          <el-input
            v-model="searchQuery"
            :placeholder="$t('controlCenter.userManagement.searchPlaceholder')"
            prefix-icon="el-icon-search"
            class="search-bar"
          />
          <el-checkbox
            v-model="showUnassignable"
            class="show-unassignable-toggle"
          >
            {{ $t("controlCenter.userManagement.showUnassignableUsers") }}
          </el-checkbox>
          <button class="btn-profile" @click="showPersonalInfo = true">
            <i class="fas fa-user-circle"></i>
            <span>{{ $t("controlCenter.userManagement.myProfile") }}</span>
          </button>
          <button
            class="btn-create-auditor-toolbar"
            @click="openCreateAuditorDialog"
          >
            <i class="fas fa-plus-circle"></i>
            <span>{{ $t("controlCenter.userManagement.createAuditor") }}</span>
          </button>
        </div>
      </div>

      <div class="table-wrapper">
        <el-table
          :data="pagedUsers"
          v-loading="loading.users"
          style="width: 100%"
          :empty-text="$t('controlCenter.userManagement.noAuditorsFound')"
          stripe
        >
          <!-- Name Column -->
          <el-table-column
            :label="$t('controlCenter.userManagement.userName')"
            min-width="150"
            fixed="left"
          >
            <template slot-scope="scope">
              <div class="user-info">
                <div
                  class="user-avatar"
                  :style="{
                    backgroundColor: getAvatarColor(scope.row.firstName),
                  }"
                >
                  {{ scope.row.firstName.charAt(0)
                  }}{{ scope.row.lastName.charAt(0) }}
                </div>
                <div>
                  <div class="user-name">
                    {{ scope.row.firstName }} {{ scope.row.lastName }}
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- Email Column -->
          <el-table-column
            :label="$t('controlCenter.userManagement.email')"
            prop="email"
            min-width="180"
          >
          </el-table-column>

          <!-- Audit User Type Column -->
          <el-table-column
            :label="
              $t('controlCenter.auditorSeatManagement.fields.auditUserType')
            "
            width="170"
            align="center"
          >
            <template slot-scope="scope">
              <el-tag
                v-if="scope.row.auditUserType"
                :type="getAuditUserTypeTagType(scope.row.auditUserType)"
                size="mini"
                effect="plain"
              >
                {{ getAuditUserTypeLabel(scope.row.auditUserType) }}
              </el-tag>
              <span v-else class="not-audit-user-text">
                {{
                  $t(
                    "controlCenter.auditorSeatManagement.fields.notAuditUserPlaceholder"
                  )
                }}
              </span>
            </template>
          </el-table-column>

          <!-- Audit User No. Column -->
          <el-table-column
            :label="
              $t('controlCenter.auditorSeatManagement.fields.auditUserNumeral')
            "
            width="180"
            align="center"
          >
            <template slot-scope="scope">
              <span>{{
                scope.row.auditUserNumeral ||
                $t(
                  "controlCenter.auditorSeatManagement.auditUserTypes.placeholder"
                )
              }}</span>
            </template>
          </el-table-column>

          <!-- Seat Status Column -->
          <el-table-column
            :label="$t('controlCenter.auditorSeatManagement.fields.hasSeat')"
            min-width="130"
            align="center"
          >
            <template slot-scope="scope">
              <el-tooltip
                :content="
                  scope.row.hasSeat
                    ? $t(
                        'controlCenter.auditorSeatManagement.fields.hasSeatTooltipTrue'
                      )
                    : $t(
                        'controlCenter.auditorSeatManagement.fields.hasSeatTooltipFalse'
                      )
                "
                placement="top"
                effect="light"
              >
                <svg
                  class="seat-icon"
                  :class="scope.row.hasSeat ? 'seat-occupied' : 'seat-empty'"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M19 9h-1V6c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v3H5c-1.1 0-2 .9-2 2v6h2v-2h14v2h2v-6c0-1.1-.9-2-2-2zm-3 0H8V6h8v3zM7 18h2v2h6v-2h2v3H7z"
                  />
                </svg>
              </el-tooltip>
            </template>
          </el-table-column>

          <!-- Last Used Column -->
          <el-table-column
            :label="$t('controlCenter.userManagement.lastUsed')"
            width="160"
          >
            <template slot-scope="scope">
              <span class="date-cell">
                {{
                  scope.row.lastLoginTime
                    ? formatDate(scope.row.lastLoginTime)
                    : $t("controlCenter.userManagement.neverLogged")
                }}
              </span>
            </template>
          </el-table-column>

          <!-- Status Column -->
          <el-table-column
            :label="$t('controlCenter.userManagement.status')"
            align="center"
            width="90"
          >
            <template slot-scope="scope">
              <span
                :class="[
                  'status-badge',
                  scope.row.enabled ? 'status-active' : 'status-inactive',
                ]"
              >
                {{
                  scope.row.enabled
                    ? $t("controlCenter.userManagement.active")
                    : $t("controlCenter.userManagement.inactive")
                }}
              </span>
            </template>
          </el-table-column>

          <!-- Assignable Column -->
          <el-table-column
            :label="
              $t(
                'controlCenter.auditorSeatManagement.dialogs.assign.assignableTag'
              )
            "
            width="120"
            align="center"
          >
            <template slot-scope="scope">
              <el-tooltip
                :content="
                  isUnassignable(scope.row)
                    ? $t(
                        'controlCenter.auditorSeatManagement.dialogs.assign.unassignableTag'
                      )
                    : $t(
                        'controlCenter.auditorSeatManagement.dialogs.assign.assignableTag'
                      )
                "
                placement="top"
                effect="light"
              >
                <svg
                  v-if="isUnassignable(scope.row)"
                  class="status-icon status-unassignable"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.93 1.69L5.69 16.93C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.93-1.69L18.31 7.07C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"
                  />
                </svg>
                <svg
                  v-else
                  class="status-icon status-assignable"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  />
                </svg>
              </el-tooltip>
            </template>
          </el-table-column>

          <!-- Actions Column -->
          <el-table-column
            :label="$t('controlCenter.userManagement.actions')"
            align="center"
            width="120"
            fixed="right"
          >
            <template slot-scope="scope">
              <el-dropdown
                @command="handleCommand($event, scope.row)"
                trigger="click"
              >
                <el-button size="small" type="default">
                  {{ $t("controlCenter.userManagement.actions") }}
                  <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="permissions">
                    <i class="el-icon-setting"></i>
                    {{ $t("controlCenter.userManagement.permissionSetting") }}
                  </el-dropdown-item>
                  <el-dropdown-item command="changePassword">
                    <i class="el-icon-lock"></i>
                    {{ $t("controlCenter.userManagement.changePassword") }}
                  </el-dropdown-item>
                  <el-dropdown-item command="assignSeat" divided>
                    <i class="el-icon-s-check"></i>
                    {{ $t("controlCenter.userManagement.assignSeat") }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
          :current-page="pageInfo.number + 1"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageInfo.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalFiltered"
        />
      </div>
    </div>

    <!-- 席位列表 -->
    <AuditorSeatManagement />

    <!-- Assign Seat Dialog -->
    <el-dialog
      :title="$t('controlCenter.userManagement.assignSeat')"
      :visible.sync="seatDialogVisible"
      width="420px"
      append-to-body
    >
      <div v-loading="seatLoading">
        <el-form v-if="availableSeats.length" label-position="top">
          <el-form-item
            :label="$t('controlCenter.userManagement.selectSeat')"
          >
            <el-select
              v-model="selectedSeatId"
              :placeholder="$t('controlCenter.userManagement.selectSeat')"
              style="width: 100%"
            >
              <el-option
                v-for="seat in availableSeats"
                :key="seat.id"
                :value="seat.id"
                :label="seat.name"
              ></el-option>
            </el-select>
          </el-form-item>
        </el-form>
        <p v-else-if="!seatLoading" class="empty-tip">
          {{ $t("controlCenter.userManagement.noAvailableSeats") }}
        </p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="seatDialogVisible = false">
          {{ $t("controlCenter.common.cancel") }}
        </el-button>
        <el-button
          type="primary"
          :disabled="!selectedSeatId"
          @click="confirmAssignSeat"
        >
          {{ $t("controlCenter.common.confirm") }}
        </el-button>
      </span>
    </el-dialog>

    <!-- Edit/Add User Modal -->
    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            {{
              isEditMode
                ? $t("controlCenter.userManagement.editAuditor")
                : $t("controlCenter.userManagement.addAuditorModalTitle")
            }}
          </h2>
          <button class="close-button" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSave">
            <div class="form-group">
              <label for="firstName">{{
                $t("controlCenter.userManagement.firstName")
              }}</label>
              <input
                type="text"
                id="firstName"
                v-model="currentUser.firstName"
                class="form-control"
                required
              />
            </div>
            <div class="form-group">
              <label for="lastName">{{
                $t("controlCenter.userManagement.lastName")
              }}</label>
              <input
                type="text"
                id="lastName"
                v-model="currentUser.lastName"
                class="form-control"
                required
              />
            </div>
            <div class="form-group">
              <label for="email">{{
                $t("controlCenter.userManagement.email")
              }}</label>
              <input
                type="email"
                id="email"
                v-model="currentUser.email"
                class="form-control"
                required
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModal">
            {{ $t("controlCenter.userManagement.cancel") }}
          </button>
          <button
            class="btn btn-primary"
            @click="handleSave"
            :disabled="saving"
          >
            <span v-if="saving">{{
              $t("controlCenter.userManagement.saving")
            }}</span>
            <span v-else>{{
              $t("controlCenter.userManagement.saveChanges")
            }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Change Password Modal -->
    <div
      v-if="isPasswordModalOpen"
      class="modal-overlay"
      @click.self="closePasswordModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            {{ $t("controlCenter.userManagement.changePasswordTitle") }}
          </h2>
          <button class="close-button" @click="closePasswordModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handlePasswordSave">
            <div class="form-group">
              <label for="currentPassword">
                {{ $t("controlCenter.userManagement.currentPassword") }}
              </label>
              <input
                type="password"
                id="currentPassword"
                v-model="passwordForm.currentPassword"
                class="form-control"
                required
              />
            </div>
            <div class="form-group">
              <label for="newPassword">
                {{ $t("controlCenter.userManagement.newPassword") }}
              </label>
              <input
                type="password"
                id="newPassword"
                v-model="passwordForm.newPassword"
                class="form-control"
                required
              />
              <small class="form-hint">
                {{ $t("controlCenter.userManagement.passwordHint") }}
              </small>
            </div>
            <div class="form-group">
              <label for="confirmNewPassword">
                {{ $t("controlCenter.userManagement.confirmPassword") }}
              </label>
              <input
                type="password"
                id="confirmNewPassword"
                v-model="passwordForm.confirmPassword"
                class="form-control"
                required
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closePasswordModal">
            {{ $t("controlCenter.userManagement.cancel") }}
          </button>
          <button
            class="btn btn-primary"
            @click="handlePasswordSave"
            :disabled="passwordSaving"
          >
            <span v-if="passwordSaving">{{
              $t("controlCenter.userManagement.saving")
            }}</span>
            <span v-else>{{
              $t("controlCenter.userManagement.changePassword")
            }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Personal Information Modal -->
    <div
      v-if="showPersonalInfo"
      class="modal-overlay"
      @click.self="showPersonalInfo = false"
    >
      <div class="modal-content personal-info-modal">
        <div class="modal-header">
          <h2 class="modal-title">
            {{ $t("controlCenter.userManagement.myProfile") }}
          </h2>
          <button class="close-button" @click="showPersonalInfo = false">
            &times;
          </button>
        </div>
        <div class="modal-body personal-info-body" v-if="accountData">
          <!-- Administrator Info Section -->
          <div class="info-section">
            <h3 class="section-title">
              {{ $t("controlCenter.userManagement.personalInfo") }}
            </h3>
            <div class="info-grid">
              <div class="info-item">
                <label>{{
                  $t("controlCenter.userManagement.firstName")
                }}</label>
                <span class="info-value">{{
                  accountData.administrator?.firstName
                }}</span>
              </div>
              <div class="info-item">
                <label>{{ $t("controlCenter.userManagement.lastName") }}</label>
                <span class="info-value">{{
                  accountData.administrator?.lastName
                }}</span>
              </div>
              <div class="info-item">
                <label>{{ $t("controlCenter.userManagement.email") }}</label>
                <span class="info-value">{{
                  accountData.administrator?.email
                }}</span>
              </div>
              <div class="info-item">
                <label>{{
                  $t("controlCenter.userManagement.phoneNumber")
                }}</label>
                <span class="info-value">{{
                  accountData.administrator?.phoneNumber || "-"
                }}</span>
              </div>
              <div class="info-item">
                <label>{{
                  $t("controlCenter.userManagement.nationality")
                }}</label>
                <span class="info-value">{{
                  accountData.administrator?.nationality || "-"
                }}</span>
              </div>
              <div class="info-item">
                <label>{{ $t("controlCenter.userManagement.sex") }}</label>
                <span class="info-value">{{
                  accountData.administrator?.sex || "-"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Organization Info Section -->
          <div class="info-section" v-if="accountData.organization">
            <h3 class="section-title">
              {{ $t("controlCenter.userManagement.organizationInfo") }}
            </h3>
            <div class="info-grid">
              <div class="info-item full-width">
                <label>{{
                  $t("controlCenter.userManagement.organizationName")
                }}</label>
                <span class="info-value">{{
                  accountData.organization?.name
                }}</span>
              </div>
              <div class="info-item full-width">
                <label>{{
                  $t("controlCenter.userManagement.legalName")
                }}</label>
                <span class="info-value">{{
                  accountData.organization?.legalName
                }}</span>
              </div>
              <div class="info-item">
                <label>{{
                  $t("controlCenter.userManagement.contactEmail")
                }}</label>
                <span class="info-value">{{
                  accountData.organization?.contactEmail
                }}</span>
              </div>
              <div class="info-item">
                <label>{{
                  $t("controlCenter.userManagement.phoneNumber")
                }}</label>
                <span class="info-value">{{
                  accountData.organization?.phoneNumber || "-"
                }}</span>
              </div>
              <div
                class="info-item full-width"
                v-if="accountData.organization?.address"
              >
                <label>{{ $t("controlCenter.userManagement.address") }}</label>
                <span class="info-value">
                  {{ accountData.organization.address?.addressLine1 }}<br />
                  {{ accountData.organization.address?.addressLine2 }}<br />
                  {{ accountData.organization.address?.city }},
                  {{ accountData.organization.address?.stateOrProvince }}<br />
                  {{ accountData.organization.address?.postalCode }},
                  {{ accountData.organization.address?.countryOrRegion }}
                </span>
              </div>
            </div>
          </div>

          <!-- Quota Info Section -->
          <div class="info-section">
            <h3 class="section-title">
              {{ $t("controlCenter.userManagement.quotaInfo") }}
            </h3>
            <div class="info-grid">
              <div class="info-item">
                <label>{{
                  $t("controlCenter.userManagement.maxAuditorCount")
                }}</label>
                <span class="info-value badge-info">{{
                  accountData.maxAuditorCount
                }}</span>
              </div>
              <div class="info-item">
                <label>{{
                  $t("controlCenter.userManagement.accountCreatedDate")
                }}</label>
                <span class="info-value">{{
                  formatDate(accountData.creationTime)
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showPersonalInfo = false">
            {{ $t("controlCenter.userManagement.close") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create Auditor Dialog -->
    <CreateAuditorDialog
      :isOpen="isCreateDialogOpen"
      :createAuditor="createAuditorWithLicence"
      @close="isCreateDialogOpen = false"
      @auditor-created="onAuditorCreated"
    />

    <!-- User Permission Dialog -->
    <UserPermissionDialog
      :isOpen="isPermissionDialogOpen"
      :user="selectedUser"
      @close="isPermissionDialogOpen = false"
    />
  </div>
</template>

<script>
import { userApi } from "@/api/user";
import { iamApi } from "@/api/iam";
import { accountApi } from "@/api/account";
import { paymentAdminApi } from "@/api/paymentAdmin";
import { getUserInfo } from "@/utils/auth";
import CreateAuditorDialog from "./CreateAuditorDialog.vue";
import UserPermissionDialog from "./UserPermissionDialog.vue";
import AuditorSeatManagement from "./AuditorSeatManagement.vue";

export default {
  name: "UserManagement",
  components: {
    CreateAuditorDialog,
    UserPermissionDialog,
    AuditorSeatManagement,
  },
  data() {
    // 从缓存中获取 userInfo
    const userInfo = getUserInfo();
    const accountId = userInfo?.accountId; // 从缓存获取，如果没有则使用默认值

    return {
      allUsers: [],
      licenceSummary: {
        available: 0,
      },
      searchQuery: "",
      loading: {
        users: true,
        account: true,
      },
      saving: false,
      isModalOpen: false,
      isEditMode: false,
      isCreateDialogOpen: false,
      isPermissionDialogOpen: false,
      isPasswordModalOpen: false,
      selectedUser: null,
      selectedPasswordUser: null,
      passwordSaving: false,
      passwordForm: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      currentUser: {
        id: null,
        firstName: "",
        lastName: "",
        email: "",
      },
      accountId: accountId, // 从缓存中获取 accountId
      avatarColors: [
        "#3498db",
        "#e74c3c",
        "#2ecc71",
        "#f1c40f",
        "#9b59b6",
        "#1abc9c",
      ],
      showUnassignable: false,
      seats: [],
      seatDialogVisible: false,
      seatLoading: false,
      selectedSeatId: "",
      assignSeatUser: null,
      pageInfo: {
        size: 20,
        number: 0,
        totalElements: 0,
        totalPages: 0,
      },
      showPersonalInfo: false,
      accountData: null,
    };
  },
  computed: {
    filteredUsers() {
      let result = this.allUsers;
      // 默认隐藏不可分配用户，勾选“显示不可分配的用户”后展示全部
      if (!this.showUnassignable) {
        result = result.filter((user) => !this.isUnassignable(user));
      }
      if (!this.searchQuery) {
        return result;
      }
      const lowerCaseQuery = this.searchQuery.toLowerCase();
      return result.filter(
        (user) =>
          (user.firstName || "").toLowerCase().includes(lowerCaseQuery) ||
          (user.lastName || "").toLowerCase().includes(lowerCaseQuery) ||
          (user.email || "").toLowerCase().includes(lowerCaseQuery)
      );
    },
    pagedUsers() {
      const start = this.pageInfo.number * this.pageInfo.size;
      return this.filteredUsers.slice(start, start + this.pageInfo.size);
    },
    totalFiltered() {
      return this.filteredUsers.length;
    },
    availableSeats() {
      // 仅展示未分配的席位
      return this.seats.filter((seat) => !seat.assignedAuditor);
    },
  },
  watch: {
    searchQuery() {
      this.pageInfo.number = 0;
    },
    showUnassignable() {
      this.pageInfo.number = 0;
    },
  },
  async created() {
    await this.refreshUserManagementData();
  },
  methods: {
    async refreshUserManagementData(options = {}) {
      const { resetPage = false, adjustEmptyPage = false } = options;

      if (resetPage) {
        this.pageInfo.number = 0;
      }

      await this.fetchAccountData();
      await this.fetchUsers();

      if (
        adjustEmptyPage &&
        this.pageInfo.number > 0 &&
        this.pagedUsers.length === 0
      ) {
        const lastPageNumber = Math.max(
          (this.pageInfo.totalPages || 1) - 1,
          0
        );
        this.pageInfo.number = Math.min(this.pageInfo.number - 1, lastPageNumber);
      }
    },
    async fetchUsers() {
      this.loading.users = true;
      try {
        const response = await iamApi.getIdentities(this.accountId);
        const identities = Array.isArray(response)
          ? response
          : response?.content || response?.data || [];
        // Map identities response to the component's expected fields.
        // 旧字段（type/creationTime/enabled）按“尽量映射”原则保留，
        // identities 响应未提供时为 undefined，由列渲染兜底处理。
        this.allUsers = identities.map((user) => ({
          id: user.id,
          accountId: user.accountId,
          firstName: user.fullName?.firstName || "",
          lastName: user.fullName?.lastName || "",
          email: user.email || "",
          type: user.type,
          role: user.role,
          enabled:
            user.enabled !== undefined ? user.enabled : true,
          creationTime: user.creationTime,
          lastLoginTime: user.lastLoginTime,
          auditUserType: user.auditUserType,
          auditUserNumeral: user.auditUserNumeral,
          hasSeat: user.hasSeat,
          isAuditUser: user.isAuditUser,
        }));
        if (!Array.isArray(response) && response?.page) {
          this.pageInfo = response.page;
        } else {
          this.pageInfo.totalElements = this.allUsers.length;
          this.pageInfo.totalPages = Math.max(
            1,
            Math.ceil(this.allUsers.length / this.pageInfo.size)
          );
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
        this.$message.error(this.$t("controlCenter.userManagement.loadError"));
        this.allUsers = [];
      } finally {
        this.loading.users = false;
      }
    },
    async fetchAccountData() {
      this.loading.account = true;
      try {
        const [accountResponse, licenceResponse] = await Promise.all([
          accountApi.getAccount(this.accountId),
          paymentAdminApi.getLicenceSummary(),
        ]);
        this.accountData = accountResponse; // 保存完整的账户数据
        this.licenceSummary = {
          available: licenceResponse?.available || 0,
        };
      } catch (error) {
        console.error("Failed to fetch account data:", error);
      } finally {
        this.loading.account = false;
      }
    },
    openCreateAuditorDialog() {
      this.isCreateDialogOpen = true;
    },
    createAuditorWithLicence(payload) {
      return paymentAdminApi.createAuditor(payload);
    },
    async onAuditorCreated() {
      this.isCreateDialogOpen = false;
      await this.refreshUserManagementData({ resetPage: true });
    },
    openAddModal() {
      this.isEditMode = false;
      this.currentUser = { id: null, firstName: "", lastName: "", email: "" };
      this.isModalOpen = true;
    },
    openEditModal(user) {
      this.isEditMode = true;
      this.currentUser = { ...user };
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    openPasswordModal(user) {
      this.selectedPasswordUser = user;
      this.passwordForm = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.isPasswordModalOpen = true;
    },
    closePasswordModal() {
      this.isPasswordModalOpen = false;
      this.selectedPasswordUser = null;
      this.passwordForm = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
    },
    async handlePasswordSave() {
      if (!this.selectedPasswordUser) return;
      if (!this.passwordForm.currentPassword || !this.passwordForm.newPassword) {
        this.$message.error(
          this.$t("controlCenter.userManagement.passwordRequired")
        );
        return;
      }
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.$message.error(
          this.$t("controlCenter.userManagement.passwordMismatch")
        );
        return;
      }

      this.passwordSaving = true;
      try {
        await userApi.updatePassword(this.selectedPasswordUser.id, {
          currentPassword: this.passwordForm.currentPassword,
          newPassword: this.passwordForm.newPassword,
        });
        this.$message.success(
          this.$t("controlCenter.userManagement.changePasswordSuccess")
        );
        this.closePasswordModal();
      } catch (error) {
        console.error("Failed to change password:", error);
        this.$message.error(
          this.$t("controlCenter.userManagement.changePasswordError")
        );
      } finally {
        this.passwordSaving = false;
      }
    },
    async handleSave() {
      this.saving = true;
      try {
        if (this.isEditMode) {
          // Update user email
          await userApi.updateEmail(this.currentUser.id, {
            email: this.currentUser.email,
          });

          await this.refreshUserManagementData();
          this.$message.success(
            this.$t("controlCenter.userManagement.updateSuccess")
          );
        } else {
          // Create new user
          const userData = {
            email: this.currentUser.email,
            password: "TempPassword123!", // You may want to add a password field to the form
            fullName: {
              firstName: this.currentUser.firstName,
              lastName: this.currentUser.lastName,
            },
            sex: "UNKNOWN",
            enabled: true,
          };

          await userApi.createUser(userData);
          await this.refreshUserManagementData({ resetPage: true });
          this.$message.success(
            this.$t("controlCenter.userManagement.addSuccess")
          );
        }
        this.closeModal();
      } catch (error) {
        console.error("Failed to save user:", error);
        if (error.response && error.response.status === 400) {
          this.$message.error(
            this.$t("controlCenter.userManagement.updateErrorOnceAYear")
          );
        } else {
          this.$message.error(
            this.$t("controlCenter.userManagement.saveError")
          );
        }
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(user) {
      this.$confirm(
        this.$t("controlCenter.userManagement.confirmDeleteMsg", {
          userName: `${user.firstName} ${user.lastName}`,
        }),
        this.$t("controlCenter.userManagement.confirmDeleteTitle"),
        {
          confirmButtonText: this.$t("controlCenter.userManagement.delete"),
          cancelButtonText: this.$t("controlCenter.userManagement.cancel"),
          type: "warning",
        }
      )
        .then(async () => {
          await this.deleteUser(user.id);
        })
        .catch(() => {
          // User cancelled
        });
    },
    async deleteUser(userId) {
      try {
        await userApi.deleteUser(userId);
        await this.refreshUserManagementData({ adjustEmptyPage: true });
        this.$message.success(
          this.$t("controlCenter.userManagement.deleteSuccess")
        );
      } catch (error) {
        console.error("Failed to delete user:", error);
        this.$message.error(
          this.$t("controlCenter.userManagement.deleteError")
        );
      }
    },
    getAvatarColor(name) {
      const index = (name || "").charCodeAt(0) % this.avatarColors.length;
      return this.avatarColors[index];
    },
    // 判断该身份是否不可分配为审计员席位
    // 规则：已是 audit 用户但 auditUserType 不是 AUDITOR 时不可分配
    isUnassignable(user) {
      return !!(
        user &&
        user.isAuditUser === true &&
        user.auditUserType &&
        user.auditUserType !== "AUDITOR"
      );
    },
    getAuditUserTypeLabel(type) {
      if (!type) {
        return this.$t(
          "controlCenter.auditorSeatManagement.auditUserTypes.placeholder"
        );
      }
      return (
        this.$t(
          `controlCenter.auditorSeatManagement.auditUserTypes.${type}`
        ) || type
      );
    },
    getAuditUserTypeTagType(type) {
      if (type === "AUDITOR") return "success";
      if (!type) return "info";
      return "danger";
    },
    formatDate(timestamp) {
      if (!timestamp)
        return this.$t("controlCenter.userManagement.neverLogged");
      const date = new Date(timestamp);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
    handleCommand(command, user) {
      if (command === "permissions") {
        this.selectedUser = user;
        this.isPermissionDialogOpen = true;
      } else if (command === "changePassword") {
        this.openPasswordModal(user);
      } else if (command === "assignSeat") {
        this.openAssignSeatDialog(user);
      }
    },
    // 打开“分配席位”弹窗并拉取席位列表
    async openAssignSeatDialog(user) {
      // 已是 audit 用户但审计用户类型不是 AUDITOR → 不可分配
      if (this.isUnassignable(user)) {
        try {
          await this.$alert(
            this.$t('controlCenter.userManagement.assignSeatDeniedMessage'),
            this.$t('controlCenter.userManagement.assignSeatDeniedTitle'),
            {
              confirmButtonText: this.$t('controlCenter.common.confirm'),
              type: 'warning'
            }
          )
        } catch (_) {
          // 用户取消/关闭弹窗，静默
        }
        return
      }
      this.assignSeatUser = user;
      this.selectedSeatId = "";
      this.seatDialogVisible = true;
      this.seatLoading = true;
      try {
        const subscription = await iamApi.getSubscription(this.accountId);
        this.seats = (subscription && subscription.auditorSeats) || [];
      } catch (error) {
        console.error("Failed to fetch seats:", error);
        this.$message.error(
          this.$t("controlCenter.userManagement.loadError")
        );
        this.seats = [];
      } finally {
        this.seatLoading = false;
      }
    },
    // 选择席位并确定后进入 Assign Auditor 页面
    confirmAssignSeat() {
      if (!this.selectedSeatId) return;
      const seatId = this.selectedSeatId;
      const identityId = this.assignSeatUser?.id;
      this.seatDialogVisible = false;
      const query = { tab: "auditorSeatAssign", seatId };
      if (identityId) query.identityId = identityId;
      this.$router
        .push({
          path: "/controlCenter",
          query,
        })
        .catch(() => {});
    },
    handlePageChange(page) {
      this.pageInfo.number = page - 1; // Element UI uses 1-based, internal uses 0-based
    },
    handleSizeChange(size) {
      this.pageInfo.size = size;
      this.pageInfo.number = 0; // Reset to first page when changing page size
    },
  },
};
</script>

<style scoped>
.main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.main-header h1 {
  margin: 0;
  color: #2c3e50;
}

.main-header .subtitle {
  margin: 8px 0 0 0;
  color: #7f8c8d;
  font-size: 14px;
}

.loading-container,
.no-results {
  text-align: center;
  padding: 40px;
  color: var(--dark-gray);
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: rgba(52, 152, 219, 0.9);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  white-space: nowrap;
  height: fit-content;
}

.btn-create:hover {
  background-color: #3498db;
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.5);
  transform: translateY(-2px);
}

.btn-create:active {
  transform: translateY(0);
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}
.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: modal-fade-in 0.3s ease;
}
.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}
.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--dark-gray);
}
.modal-body {
  padding: 25px;
}
.modal-footer {
  padding: 20px 25px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.btn-secondary {
  background-color: var(--light-gray);
  color: var(--dark-gray);
}
.form-group {
  margin-bottom: 20px;
}
.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #34495e;
}
.form-control {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
}
.form-control:focus {
  outline: none;
  border-color: var(--secondary-color);
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
}

.form-hint {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #7f8c8d;
}

.search-controls {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.search-bar {
  flex: 1;
}

.show-unassignable-toggle {
  white-space: nowrap;
}

.empty-tip {
  margin: 0;
  padding: 12px 0;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  padding: 10px 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
}

.user-name {
  font-weight: 500;
  color: #2c3e50;
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-admin {
  background-color: #e3f2fd;
  color: #1976d2;
}

.type-auditor {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.date-cell {
  font-size: 13px;
  color: #7f8c8d;
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background-color: #d4edda;
  color: #155724;
}

.status-inactive {
  background-color: #f8d7da;
  color: #721c24;
}

/* Audit User Type 列：非 audit 用户的占位提示 */
.not-audit-user-text {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}

/* Seat Status 列图标（内联 SVG） */
.seat-icon {
  display: inline-block;
  vertical-align: middle;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.seat-icon.seat-occupied {
  color: #f59e0b;
}

.seat-icon.seat-empty {
  color: #d1d5db;
}

/* Assignable 列图标（内联 SVG） */
.status-icon {
  display: inline-block;
  vertical-align: middle;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.status-icon.status-assignable {
  color: #059669;
}

.status-icon.status-unassignable {
  color: #dc2626;
}

.btn-profile {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background-color: #f0f2f5;
  color: #333;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  white-space: nowrap;
  height: fit-content;
}

.btn-profile:hover {
  background-color: #e0e0e0;
  border-color: #c0c0c0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.btn-profile:active {
  transform: translateY(0);
}

.btn-create-auditor-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
  height: fit-content;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-create-auditor-toolbar:hover {
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(-1px);
}

.btn-create-auditor-toolbar:active {
  transform: translateY(0);
}

.btn-create-auditor-toolbar.disabled {
  opacity: 0.58;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.personal-info-modal .modal-content {
  max-width: 1200px;
  width: 96%;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.personal-info-modal .modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
  flex: 1;
}

.personal-info-modal .info-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8eaed;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.personal-info-modal .info-section:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.personal-info-modal .section-title {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  border-bottom: 2px solid #667eea;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.personal-info-modal .section-title::before {
  content: "";
  width: 4px;
  height: 14px;
  background: linear-gradient(180deg, #667eea, #764ba2);
  border-radius: 2px;
}

.personal-info-modal .info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
}

.personal-info-modal .info-item {
  display: flex;
  flex-direction: column;
  padding: 8px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
  transition: all 0.2s ease;
  gap: 2px;
}

.personal-info-modal .info-item:hover {
  background-color: rgba(102, 126, 234, 0.05);
}

.personal-info-modal .info-item label {
  font-size: 11px;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.8;
}

.personal-info-modal .info-value {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 600;
  word-break: break-word;
  line-height: 1.4;
}

.personal-info-modal .badge-info {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 700;
  display: inline-block;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  height: fit-content;
}

.personal-info-modal .full-width {
  grid-column: 1 / -1;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 20px;
  }

  .modal-overlay {
    align-items: flex-start;
    overflow-y: auto;
    padding: 70px 10px 14px;
  }

  .modal-content {
    width: 100%;
    max-width: none;
    max-height: calc(100dvh - 84px);
    display: flex;
    flex-direction: column;
  }

  .modal-body {
    overflow-y: auto;
    padding: 14px;
  }

  .modal-title {
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .search-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .search-bar {
    width: 100%;
  }

  .btn-profile {
    width: 100%;
    justify-content: center;
  }

  .btn-create-auditor-toolbar {
    width: 100%;
    justify-content: center;
  }

  .table-wrapper {
    border-radius: 8px;
  }

  .table-wrapper .el-table {
    min-width: 980px;
  }

  .user-info {
    align-items: flex-start;
    min-width: 0;
  }

  .user-name {
    overflow-wrap: anywhere;
  }

  .el-table .cell .el-button {
    margin: 3px 4px 3px 0;
  }

  .pagination-container {
    justify-content: center;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .personal-info-modal .modal-content {
    width: 98%;
    max-height: 86vh;
  }

  .personal-info-modal .modal-body {
    padding: 12px;
    gap: 10px;
  }

  .personal-info-modal .info-section {
    padding: 10px 12px;
  }

  .personal-info-modal .section-title {
    font-size: 12px;
    margin-bottom: 8px;
  }

  .personal-info-modal .info-grid {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }

  .personal-info-modal .info-item {
    padding: 6px;
  }

  .personal-info-modal .info-item label {
    font-size: 10px;
  }

  .personal-info-modal .info-value {
    font-size: 12px;
  }

  .modal-header {
    padding: 12px 14px;
  }

  .modal-title {
    font-size: 15px;
  }

  .modal-footer {
    padding: 10px 12px;
    gap: 8px;
    flex-direction: column-reverse;
  }

  .modal-footer .btn {
    width: 100%;
    justify-content: center;
  }

  .close-button {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .main-header {
    margin-bottom: 20px;
  }

  .main-header h1 {
    font-size: 20px;
  }

  .main-header .subtitle {
    font-size: 13px;
  }

  .search-controls {
    gap: 8px;
  }

  .card {
    margin-bottom: 15px;
  }

  .card-header {
    padding: 12px;
  }

  /* Ensure table is scrollable on mobile */
  .table-wrapper {
    margin: 0 -12px;
    padding: 0 12px;
  }

  .el-table {
    font-size: 12px;
    min-width: 900px; /* Ensure table has minimum width for horizontal scroll */
  }

  .el-table th,
  .el-table td {
    padding: 8px 5px;
  }

  .el-table .cell {
    line-height: 1.4;
  }

  .user-avatar {
    width: 30px;
    height: 30px;
    font-size: 11px;
  }

  .user-name {
    font-size: 13px;
  }

  .type-badge,
  .status-badge {
    font-size: 10px;
    padding: 3px 8px;
  }

  .date-cell {
    font-size: 11px;
  }

  .el-button--small {
    padding: 6px 10px;
    font-size: 12px;
  }

  .pagination-container {
    margin-top: 15px;
    padding: 5px 0;
  }

  .el-pagination {
    padding: 5px;
  }

  .el-pagination__sizes,
  .el-pagination__jump {
    display: none;
  }

  .modal-content {
    width: 95%;
    max-width: none;
  }

  .form-group {
    margin-bottom: 15px;
  }

  .form-group label {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .form-control {
    padding: 8px 12px;
    font-size: 13px;
  }

  .personal-info-modal .modal-content {
    width: 100%;
    max-height: 90vh;
    border-radius: 16px 16px 0 0;
    margin-bottom: 0;
  }

  .personal-info-modal .modal-body {
    padding: 10px;
    gap: 8px;
  }

  .personal-info-modal .info-section {
    padding: 10px;
  }

  .personal-info-modal .section-title {
    font-size: 11px;
    margin-bottom: 6px;
  }

  .personal-info-modal .info-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .personal-info-modal .info-item {
    padding: 6px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .personal-info-modal .info-item label {
    font-size: 9px;
    min-width: 70px;
  }

  .personal-info-modal .info-value {
    font-size: 11px;
    text-align: right;
  }

  .modal-header {
    padding: 10px;
  }

  .modal-title {
    font-size: 13px;
  }

  .modal-footer {
    padding: 8px 10px;
    flex-direction: column;
  }

  .btn-secondary,
  .btn-primary {
    width: 100%;
    font-size: 13px;
    padding: 8px 12px;
  }
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
